import {
  Box,
  Button,
  HStack,
  Text,
  Textarea,
  useColorModeValue,
  IconButton,
  Select,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import axios from 'axios';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FiBold,
  FiItalic,
  FiUnderline,
  FiLink,
  FiList,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
} from 'react-icons/fi';
import Card from 'components/card/Card';
import {
  extractFinalBody,
  extractStyles,
  injectStylesOnce,
  sanitizeHtml,
} from '../../../utils/htmlContentUtils';

export default function AboutUs() {
  const [content, setContent] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const textareaRef = React.useRef(null);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem('token');

  // 1. Fetch Content
  const fetchAboutUs = React.useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = `${baseUrl}api/admin/about-us`;
      if (!baseUrl || !token) {
        throw new Error('Missing base URL or authentication token');
      }
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.status) {
        const receivedData = response.data.data
          ? response.data.data.content
          : response.data.content;
        const cleanedContent = extractFinalBody(receivedData);
        setContent(cleanedContent || '');

        const styles = extractStyles(receivedData, '.about-us-html-root');
        injectStylesOnce(styles, 'about-us-html-styles');
      }
      console.log('FULL RESPONSE:', response.data);
      setLoading(false);
    } catch (err) {
      console.error('Fetch About Us Error:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/');
      } else {
        setError(err.message || 'Failed to fetch content');
        setLoading(false);
      }
    }
  }, [baseUrl, token, navigate]);

  React.useEffect(() => {
    fetchAboutUs();
  }, [fetchAboutUs]);

  // 2. Save Content
  const handleSave = async () => {
    try {
      const apiUrl = `${baseUrl}api/admin/about-us`;
      const response = await axios.post(
        apiUrl,
        { content },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status) {
        toast.success('About Us updated successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
        setIsEditing(false);
        fetchAboutUs();
      }
    } catch (err) {
      console.error('Update Error:', err);
      toast.error('Failed to update content');
    }
  };

  // 3. Formatting Logic (Privacy Policy Style)
  const applyFormatting = (format) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.slice(start, end);

    if (!selectedText) {
      toast.warn('Please select text to format');
      return;
    }

    let newText = '';
    switch (format) {
      case 'bold':
        newText = `<b>${selectedText}</b>`;
        break;
      case 'italic':
        newText = `<i>${selectedText}</i>`;
        break;
      case 'underline':
        newText = `<u>${selectedText}</u>`;
        break;
      case 'heading1':
        newText = `<h1>${selectedText}</h1>`;
        break;
      case 'heading2':
        newText = `<h2>${selectedText}</h2>`;
        break;
      default:
        newText = selectedText;
    }

    const updatedContent =
      content.slice(0, start) + newText + content.slice(end);
    setContent(updatedContent);

    setTimeout(() => {
      textarea.selectionStart = start;
      textarea.selectionEnd = start + newText.length;
      textarea.focus();
    }, 0);
  };

  if (loading) {
    return (
      <Card flexDirection="column" w="100%" px="0px" mt="100px">
        <Flex justify="center" align="center" p="50px">
          <Spinner size="xl" />
        </Flex>
      </Card>
    );
  }

  if (error) {
    return (
      <Card flexDirection="column" w="100%" px="0px" mt="100px">
        <Box px="25px" mb="20px">
          <Text color="red.500" fontSize="18px" fontWeight="600">
            {error}
          </Text>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      mt="100px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Box px="25px" mb="20px">
        <Text color={textColor} fontSize="22px" fontWeight="700" mb="20px">
          About Us
        </Text>

        {isEditing ? (
          <Box>
            {/* Toolbar - Exactly like Privacy Policy */}
            <HStack spacing={2} mb={2}>
              <Select
                placeholder="Normal"
                size="sm"
                w="120px"
                onChange={(e) => {
                  if (e.target.value === 'Heading 1')
                    applyFormatting('heading1');
                  if (e.target.value === 'Heading 2')
                    applyFormatting('heading2');
                }}
              >
                <option value="Heading 1">Heading 1</option>
                <option value="Heading 2">Heading 2</option>
                <option value="Normal">Normal</option>
              </Select>
              <IconButton
                icon={<FiBold />}
                size="sm"
                onClick={() => applyFormatting('bold')}
                aria-label="Bold"
              />
              <IconButton
                icon={<FiItalic />}
                size="sm"
                onClick={() => applyFormatting('italic')}
                aria-label="Italic"
              />
              <IconButton
                icon={<FiUnderline />}
                size="sm"
                onClick={() => applyFormatting('underline')}
                aria-label="Underline"
              />
              <IconButton icon={<FiLink />} size="sm" aria-label="Link" />
              <IconButton icon={<FiList />} size="sm" aria-label="List" />
              <IconButton
                icon={<FiAlignLeft />}
                size="sm"
                aria-label="Align Left"
              />
              <IconButton
                icon={<FiAlignCenter />}
                size="sm"
                aria-label="Align Center"
              />
              <IconButton
                icon={<FiAlignRight />}
                size="sm"
                aria-label="Align Right"
              />
            </HStack>

            <Textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              minH="250px"
              placeholder="Enter About Us content here..."
              mb={4}
              bg="white"
              color="black"
              borderColor="gray.300"
              _placeholder={{ color: 'gray.400' }}
              _focus={{
                borderColor: 'brand.500',
                boxShadow: '0 0 0 1px #3182ce',
              }}
            />

            <HStack spacing={3}>
              <Button colorScheme="blue" onClick={handleSave}>
                Save
              </Button>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </HStack>
          </Box>
        ) : (
          <Box
            className="about-us-html-root"
            bg="white"
            color="black"
            p={4}
            borderRadius="16px"
            border="1px solid"
            borderColor="gray.200"
          >
            <Box
              fontSize="md"
              lineHeight="1.8"
              mb={4}
              color="black"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
            />
            <Button colorScheme="teal" onClick={() => setIsEditing(true)}>
              Edit About Us
            </Button>
          </Box>
        )}
      </Box>
      <ToastContainer position="top-right" theme="colored" />
    </Card>
  );
}
